import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import Dialog from '@/components/Dialog'
import LoadingSpinner from '@/components/LoadingSpinner'
import { JoinRequestResponse } from '@/lib/definitions/api.types'
import { noop, parseResponseErrorsAndNotify } from '@/lib/misc/Utils'
import { REFRESH_USER, useAuth } from '@/lib/provider/AuthProvider'
import { MembershipService } from '@/lib/services/MembershipService'
import { CompanyCard } from '@/pages/settings/partials/CompanyOnboarding'

const membershipService = new MembershipService()

interface CompanyInvitationsProps {
  invitations: JoinRequestResponse[]
  onDismiss?: () => void
}

const CompanyInvitations = ({
  invitations,
  onDismiss,
}: CompanyInvitationsProps) => {
  const queryClient = useQueryClient()
  const [auth, authDispatch] = useAuth()
  const invitation = invitations[0]
  const hasCompany = Boolean(auth.user?.customer_id)

  const settled = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchMyInvitations'] })
    queryClient.invalidateQueries({ queryKey: ['fetchMemberships'] })
  }

  const accepted = () => {
    settled()
    authDispatch({ type: REFRESH_USER })
  }

  const { mutate: accept, isPending: isAccepting } = useMutation({
    mutationFn: (requestId: number) =>
      membershipService.acceptInvitation(requestId),
    onError: (error: AxiosError) => parseResponseErrorsAndNotify(error),
    onSuccess: accepted,
  })

  const { mutate: decline, isPending: isDeclining } = useMutation({
    mutationFn: (requestId: number) =>
      membershipService.declineInvitation(requestId),
    onError: (error: AxiosError) => parseResponseErrorsAndNotify(error),
    onSuccess: settled,
  })

  if (!invitation) return null

  const isBusy = isAccepting || isDeclining

  return (
    <Dialog
      title="You have an invitation"
      message={
        hasCompany
          ? `${invitation.customer_name} invited you to join them. Accepting adds the company to your account — you keep the one you already have.`
          : `${invitation.customer_name} invited you to join them. Accepting makes it your company, so you can skip setting one up.`
      }
      hasButtons={false}
      closable={Boolean(onDismiss)}
      dismissOnBackdrop={Boolean(onDismiss)}
      onConfirm={noop}
      onDismiss={onDismiss ?? noop}
    >
      <div data-cy="company-invitations" className="mt-5">
        <CompanyCard name={invitation.customer_name} note="Invited you" />

        <div className="mt-5 flex justify-end gap-3">
          {onDismiss && (
            <button
              type="button"
              data-cy="invitation-later-btn"
              disabled={isBusy}
              onClick={onDismiss}
              className="mr-auto cursor-pointer rounded-lg px-4 py-2 text-sm font-medium text-neutral-50 transition-colors hover:bg-neutral-05 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Later
            </button>
          )}
          <button
            type="button"
            data-cy="invitation-decline-btn"
            disabled={isBusy}
            onClick={() => decline(invitation.id)}
            className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-neutral-20 px-4 py-2 text-sm font-medium transition-colors hover:bg-neutral-05 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDeclining && (
              <LoadingSpinner className="size-4" interlinkLogo={false} />
            )}
            Decline
          </button>
          <button
            type="button"
            data-cy="invitation-accept-btn"
            disabled={isBusy}
            onClick={() => accept(invitation.id)}
            className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-accent-100 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isAccepting && (
              <LoadingSpinner className="size-4" interlinkLogo={false} />
            )}
            Accept
          </button>
        </div>

        {invitations.length > 1 && (
          <p className="mt-4 text-sm text-neutral-50">
            {invitations.length - 1} more invitation
            {invitations.length > 2 ? 's' : ''} after this one.
          </p>
        )}
      </div>
    </Dialog>
  )
}

export default CompanyInvitations
